"use client";

import * as React from "react";
import { Tabs as TabsPrimitive } from "@base-ui/react/tabs";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@ds/lib/utils";

/**
 * Tabs — Capture Sheet field 12.7.
 *
 * Two distinct forms in the evidence, and they are NOT the same component
 * wearing different colours:
 *  - `track` sits inside content: a light track, selected segment a raised
 *    white pill. This is a true segmented control.
 *  - `bare`  sits in a header: no track at all, selected label wrapped in a
 *    dark filled pill.
 */
const listVariants = cva("inline-flex items-center", {
  variants: {
    variant: {
      track: "w-full gap-1 rounded-pill bg-muted p-1",
      bare: "w-full justify-between gap-1",
    },
  },
  defaultVariants: { variant: "track" },
});

const triggerVariants = cva(
  [
    "inline-flex flex-1 items-center justify-center whitespace-nowrap rounded-pill",
    "text-row transition-colors duration-200 ease-out-ds",
    "focus-visible:outline-ring focus-visible:outline-2 focus-visible:outline-offset-2 outline-none",
    "disabled:pointer-events-none disabled:opacity-50",
  ],
  {
    variants: {
      variant: {
        track:
          "px-4 py-2 text-muted-foreground data-active:bg-background data-active:text-foreground",
        bare: "px-4 py-2 text-muted-foreground data-active:bg-primary data-active:text-primary-foreground",
      },
    },
    defaultVariants: { variant: "track" },
  },
);

type TabsVariant = "track" | "bare";
const TabsVariantContext = React.createContext<TabsVariant>("track");

function Tabs({
  className,
  variant = "track",
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Root> & { variant?: TabsVariant }) {
  return (
    <TabsVariantContext value={variant}>
      <TabsPrimitive.Root data-slot="tabs" className={cn("flex flex-col gap-4", className)} {...props} />
    </TabsVariantContext>
  );
}

function TabsList({
  className,
  variant,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.List> & VariantProps<typeof listVariants>) {
  const ctx = React.use(TabsVariantContext);
  return (
    <TabsPrimitive.List
      data-slot="tabs-list"
      className={cn(listVariants({ variant: variant ?? ctx }), className)}
      {...props}
    />
  );
}

function TabsTrigger({
  className,
  variant,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Tab> & VariantProps<typeof triggerVariants>) {
  const ctx = React.use(TabsVariantContext);
  return (
    <TabsPrimitive.Tab
      data-slot="tabs-trigger"
      className={cn(triggerVariants({ variant: variant ?? ctx }), className)}
      {...props}
    />
  );
}

function TabsContent({ className, ...props }: React.ComponentProps<typeof TabsPrimitive.Panel>) {
  return (
    <TabsPrimitive.Panel
      data-slot="tabs-content"
      className={cn("outline-none", className)}
      {...props}
    />
  );
}

export { Tabs, TabsList, TabsTrigger, TabsContent };
